import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, Plus, X } from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
import EmptyState from './EmptyState';

const fmt     = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// ─── Setup Modal (first-time) ─────────────────────────────────────────────────

function SetupModal({ onSave, onClose }) {
  const [company,      setCompany]      = useState('');
  const [ticker,       setTicker]       = useState('');
  const [currentPrice, setCurrentPrice] = useState('');
  const [grantDate,    setGrantDate]    = useState('');
  const [totalGranted, setTotalGranted] = useState('');
  const [taxRate,      setTaxRate]      = useState('0.37');

  function handleSave() {
    if (!company || !ticker || !currentPrice || !grantDate || !totalGranted) return;
    onSave({
      company,
      ticker:           ticker.toUpperCase(),
      currentPrice:     Number(currentPrice),
      grantDate,
      totalGranted:     Number(totalGranted),
      taxRate:          Number(taxRate),
      vestingSchedule:  [],
    });
  }

  const ready = company && ticker && currentPrice && grantDate && totalGranted;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-slate-100">Set Up RSU Tracker</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-slate-200 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Company Name</label>
              <input
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="e.g. Acme Corp"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Ticker Symbol</label>
              <input
                type="text"
                value={ticker}
                onChange={(e) => setTicker(e.target.value)}
                placeholder="e.g. ACME"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Current Price (USD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={currentPrice}
                onChange={(e) => setCurrentPrice(e.target.value)}
                placeholder="0.00"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Total Shares Granted</label>
              <input
                type="number"
                min="0"
                value={totalGranted}
                onChange={(e) => setTotalGranted(e.target.value)}
                placeholder="0"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Grant Date</label>
              <input
                type="date"
                value={grantDate}
                onChange={(e) => setGrantDate(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Tax Rate</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.01"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                placeholder="0.37"
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={!ready}
          className="mt-6 w-full py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
        >
          Create RSU Tracker
        </button>
      </div>
    </div>
  );
}

// ─── Add Vest Event Modal ─────────────────────────────────────────────────────

function VestEventModal({ onSave, onClose }) {
  const [date,        setDate]        = useState('');
  const [shares,      setShares]      = useState('');
  const [priceAtVest, setPriceAtVest] = useState('');

  function handleSave() {
    if (!date || !shares) return;
    const event = {
      date,
      shares:     Number(shares),
      vested:     false,
    };
    if (priceAtVest) event.priceAtVest = Number(priceAtVest);
    onSave(event);
  }

  const ready = date && shares;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-slate-100">Add Vest Event</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-slate-200 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Vest Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Shares</label>
            <input
              type="number"
              min="1"
              value={shares}
              onChange={(e) => setShares(e.target.value)}
              placeholder="e.g. 250"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Price at Vest (USD) <span className="text-slate-600">— optional</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={priceAtVest}
              onChange={(e) => setPriceAtVest(e.target.value)}
              placeholder="Leave blank to use current price"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={!ready}
          className="mt-6 w-full py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
        >
          Add Vest Event
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function RSUs() {
  const { rsusData, updateSection } = useUserData();

  const [showSetup,    setShowSetup]    = useState(false);
  const [showVestForm, setShowVestForm] = useState(false);

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (!rsusData) {
    return (
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">RSU Tracker</h1>
          <p className="text-slate-400 text-sm mt-0.5">Track equity compensation, vesting schedules, and tax obligations.</p>
        </div>

        <EmptyState
          icon="📈"
          title="No equity data yet"
          description="Track your RSU vesting schedule, tax obligations, and grant history."
          actionLabel="Set Up RSU Tracker"
          onAction={() => setShowSetup(true)}
        />

        {showSetup && (
          <SetupModal
            onSave={(data) => {
              updateSection('rsus', data);
              setShowSetup(false);
            }}
            onClose={() => setShowSetup(false)}
          />
        )}
      </div>
    );
  }

  // ── Computed vested / unvested from schedule ────────────────────────────────
  const today = new Date().toISOString().slice(0, 10);
  const vested   = rsusData.vestingSchedule.filter(v => v.date <= today).reduce((s, v) => s + v.shares, 0);
  const unvested = (rsusData.totalGranted || 0) - vested;

  const currentValue = unvested * rsusData.currentPrice;
  const taxEstimate  = currentValue * rsusData.taxRate;

  const upcomingVest = rsusData.vestingSchedule.find(v => !v.vested && v.date > today);
  const daysUntil    = upcomingVest
    ? Math.round((new Date(upcomingVest.date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  // ── Handlers ────────────────────────────────────────────────────────────────
  function handleAddVestEvent(event) {
    updateSection('rsus', {
      ...rsusData,
      vestingSchedule: [...rsusData.vestingSchedule, event],
    });
    setShowVestForm(false);
  }

  function handleDeleteVestEvent(index) {
    const updated = rsusData.vestingSchedule.filter((_, i) => i !== index);
    updateSection('rsus', { ...rsusData, vestingSchedule: updated });
  }

  // ── Full dashboard ──────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">RSU Tracker</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          {rsusData.company} ({rsusData.ticker}) · Current price: {fmt(rsusData.currentPrice)}/share
        </p>
      </div>

      {/* Upcoming vest alert */}
      {daysUntil !== null && daysUntil <= 30 && (
        <div className="flex items-start gap-3 p-4 bg-amber-950/60 border border-amber-700/50 rounded-xl">
          <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-300">RSU Vest in {daysUntil} days</div>
            <div className="text-sm text-amber-200/70 mt-0.5">
              {upcomingVest.shares} shares vesting {fmtDate(upcomingVest.date)} — estimated value {fmt(upcomingVest.shares * rsusData.currentPrice)}.
              Set aside <strong className="text-amber-300">{fmt(upcomingVest.shares * rsusData.currentPrice * rsusData.taxRate)}</strong> for federal taxes ({Math.round(rsusData.taxRate * 100)}% bracket).
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Granted',      value: `${rsusData.totalGranted} shares`, sub: `Grant date: ${fmtDate(rsusData.grantDate)}` },
          { label: 'Vested',             value: `${vested} shares`,                sub: fmt(vested * rsusData.currentPrice) + ' current value' },
          { label: 'Unvested',           value: `${unvested} shares`,              sub: fmt(currentValue) + ' projected value' },
          { label: `Tax at Vest (${Math.round(rsusData.taxRate * 100)}%)`, value: fmt(taxEstimate), sub: 'Set this aside at vest', color: 'text-amber-400' },
        ].map(k => (
          <div key={k.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">{k.label}</div>
            <div className={`text-xl font-bold ${k.color || 'text-slate-100'}`}>{k.value}</div>
            <div className="text-xs text-slate-500 mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Vesting timeline */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-semibold text-slate-200 text-base">Vesting Schedule</h2>
          <button
            onClick={() => setShowVestForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-lg text-slate-300 text-xs font-medium transition-colors"
          >
            <Plus size={13} />
            Add Vest Event
          </button>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-slate-400 mb-2">
            <span>{vested} shares vested</span>
            <span>{unvested} shares remaining</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
            <div
              className="h-3 rounded-full bg-gradient-to-r from-blue-600 to-blue-400"
              style={{ width: `${rsusData.totalGranted > 0 ? (vested / rsusData.totalGranted) * 100 : 0}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-slate-600 mt-1">
            <span>0</span>
            <span>{rsusData.totalGranted} total</span>
          </div>
        </div>

        {/* Events */}
        {rsusData.vestingSchedule.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">
            No vest events yet. Add one above to build your vesting schedule.
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-5 top-3 bottom-3 w-px bg-slate-700" />

            <div className="space-y-4">
              {rsusData.vestingSchedule.map((event, i) => {
                const isPast   = event.date <= today;
                const isNext   = !isPast && i === rsusData.vestingSchedule.findIndex(v => v.date > today);
                const vestValue = event.shares * (event.priceAtVest || rsusData.currentPrice);
                const taxEst    = vestValue * rsusData.taxRate;

                return (
                  <div key={i} className="flex items-start gap-4 pl-2">
                    {/* Dot */}
                    <div className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isPast  ? 'bg-emerald-900/60 border-2 border-emerald-500'
                      : isNext ? 'bg-amber-900/60 border-2 border-amber-500 animate-pulse'
                      : 'bg-slate-700 border-2 border-slate-600'
                    }`}>
                      {isPast
                        ? <CheckCircle size={14} className="text-emerald-400" />
                        : isNext
                        ? <Clock size={14} className="text-amber-400" />
                        : <div className="w-2 h-2 bg-slate-500 rounded-full" />
                      }
                    </div>

                    {/* Content */}
                    <div className={`flex-1 p-4 rounded-xl border ${
                      isPast  ? 'bg-slate-700/30 border-slate-700/50'
                      : isNext ? 'bg-amber-950/30 border-amber-700/40'
                      : 'bg-slate-700/20 border-slate-700/30'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-semibold ${
                              isPast ? 'text-emerald-400' : isNext ? 'text-amber-300' : 'text-slate-400'
                            }`}>
                              {isPast ? 'Vested' : isNext ? 'Next Vest' : 'Scheduled'}
                            </span>
                            <span className="text-xs text-slate-500">{fmtDate(event.date)}</span>
                          </div>
                          <div className="text-base font-bold text-slate-200 mt-1">{event.shares} shares</div>
                          {event.priceAtVest && (
                            <div className="text-xs text-slate-500 mt-0.5">Vested at {fmt(event.priceAtVest)}/share</div>
                          )}
                        </div>

                        <div className="flex items-start gap-3">
                          <div className="text-right">
                            <div className="text-base font-bold text-slate-200">
                              {fmt(vestValue)}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {isPast ? 'at vest price' : 'at current price'}
                            </div>
                            {!isPast && (
                              <div className="text-xs text-amber-400 mt-1 font-medium">
                                ~{fmt(taxEst)} tax due
                              </div>
                            )}
                          </div>

                          {/* Delete button */}
                          <button
                            onClick={() => handleDeleteVestEvent(i)}
                            className="mt-0.5 p-1 rounded hover:bg-slate-600 text-slate-500 hover:text-rose-400 transition-colors flex-shrink-0"
                            title="Remove vest event"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Tax guide */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <h2 className="font-semibold text-slate-200 text-base mb-4">RSU Tax Guide</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { title: 'At Vest (Ordinary Income)', desc: 'RSUs are taxed as ordinary income when they vest. The value on vest date = your W-2 income. Employer withholds taxes automatically (often at 22% flat — may not be enough for high earners).', color: 'border-amber-700/40 bg-amber-950/20' },
            { title: 'After Vest (Capital Gains)', desc: 'After vesting, shares are yours. If you hold them and the price goes up, you only owe capital gains tax when you sell. Hold >1 year = long-term rates (0/15/20%). Short-term = ordinary rates.', color: 'border-blue-700/40 bg-blue-950/20' },
            { title: 'Strategy: Sell or Hold?', desc: "Many advisors say \"sell immediately\" to avoid concentration risk. Only hold if you genuinely believe in the company — you're already taking a lot of company risk with your salary.", color: 'border-purple-700/40 bg-purple-950/20' },
          ].map(tip => (
            <div key={tip.title} className={`p-4 rounded-xl border ${tip.color}`}>
              <div className="text-sm font-semibold text-slate-200 mb-2">{tip.title}</div>
              <div className="text-xs text-slate-400 leading-relaxed">{tip.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Add vest event modal */}
      {showVestForm && (
        <VestEventModal
          onSave={handleAddVestEvent}
          onClose={() => setShowVestForm(false)}
        />
      )}
    </div>
  );
}
